import { gql, useMutation } from '@apollo/client';
import { Button, useToast } from '@chakra-ui/react';
import * as React from 'react';

import {
  CancelNextMembershipTerm_CancelQueuedMembershipTerm,
  CancelNextMembershipTerm_CancelQueuedMembershipTermVariables,
} from './graphql-types/CancelNextMembershipTerm_CancelQueuedMembershipTerm';

const RELEVANT_TERMS_FRAGMENT = gql`
  fragment RelevantTermsFragment on MembershipTermV2 {
    _id
    canCurrentUserChangeRenewal
    canCurrentUserExtendEndDate
    canCurrentUserTerminate
    paymentStatus
    paymentOrder {
      _id
    }
    importantDates {
      date
      ... on SimpleMembershipTermImportantDate {
        date
        type
      }
      ... on PaymentMembershipTermImportantDate {
        date
        paymentType
      }
      ... on AutoPayMembershipTermImportantDate {
        date
        autoPayType
      }
    }
  }
`;

const CANCEL_QUEUED_MEMBERSHIP_TERM = gql`
  mutation CancelNextMembershipTerm_CancelQueuedMembershipTerm(
    $input: CancelQueuedMembershipTermInput!
  ) {
    cancelQueuedMembershipTerm(input: $input) {
      ... on CancelQueuedMembershipTermSuccess {
        user {
          _id
          membershipData {
            canCurrentUserAddMembership
            relevantMembershipTerms(input: { includeRecentlyLapsed: true }) {
              ...RelevantTermsFragment
            }
          }
          creditBalance
        }
        organization {
          _id
          membershipData {
            canCurrentUserAddMembership
            relevantMembershipTerms(input: { includeRecentlyLapsed: true }) {
              ...RelevantTermsFragment
            }
          }
          creditBalance
        }
      }
    }
  }
  ${RELEVANT_TERMS_FRAGMENT}
`;

export const CancelNextMembershipTerm: React.FC<{
  nextMembershipTermId: string;
}> = ({ nextMembershipTermId }) => {
  const toast = useToast();
  const [cancelQueuedMembershipTerm, cancelQueuedMembershipTermResult] =
    useMutation<
      CancelNextMembershipTerm_CancelQueuedMembershipTerm,
      CancelNextMembershipTerm_CancelQueuedMembershipTermVariables
    >(CANCEL_QUEUED_MEMBERSHIP_TERM, {
      variables: {
        input: {
          membershipTermId: nextMembershipTermId ?? '',
        },
      },
      onError: () =>
        toast({
          title: 'An unknown error occurred',
          status: 'error',
        }),
      onCompleted: (data) => {
        if (
          data.cancelQueuedMembershipTerm.__typename ===
          'CancelQueuedMembershipTermSuccess'
        ) {
          toast({
            title: `You successfully cancelled the upcoming membership`,
            status: 'success',
          });
        } else {
          toast({
            title: 'An unknown error occurred',
            status: 'error',
          });
        }
      },
      update(cache) {
        cache.evict({
          id: cache.identify({
            _id: nextMembershipTermId,
            __typename: 'MembershipTermV2',
          }),
        });
        cache.gc();
      },
    });

  return (
    <Button
      variant="outline"
      size="sm"
      colorScheme="gray"
      onClick={() => cancelQueuedMembershipTerm()}
      isLoading={cancelQueuedMembershipTermResult.loading}
    >
      Cancel
    </Button>
  );
};
